// CLUB LINKS: Instagram, club videos, and "only links the club gave us". PURE.
//
// ⚠️ THE ONLY URLS SAM MAY SEND are the ones the club typed into its Business Profile: join link,
// review link, Instagram, and each video. Everything else a model writes is invented, whatever it
// looks like, and is held by the verifier (priceVerifier.ts reads `urlKey` / `urlsIn` from here).

export type ParseResult = { ok: true; value: string } | { ok: false; message: string };

const HANDLE = /^[A-Za-z0-9._]{1,30}$/;
const IG_HOSTS = new Set(['instagram.com', 'www.instagram.com']);

/**
 * Instagram: empty, an https://(www.)instagram.com/<handle> URL, or a bare @handle. Normalised to
 * https://www.instagram.com/<handle> (a query string such as ?igsh=... is dropped, since a pasted
 * share link carries one). Anything else is refused with a message the editor can show.
 */
export function parseInstagramInput(input: string): ParseResult {
  const raw = (input ?? '').trim();
  if (raw === '') return { ok: true, value: '' };
  const at = raw.match(/^@([A-Za-z0-9._]{1,30})$/);
  if (at) return { ok: true, value: `https://www.instagram.com/${at[1]}` };
  const refuse: ParseResult = {
    ok: false,
    message: 'Use the club\'s Instagram link (https://www.instagram.com/yourclub) or just @yourclub.',
  };
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return refuse;
  }
  if (url.protocol !== 'https:' || !IG_HOSTS.has(url.hostname.toLowerCase())) return refuse;
  const parts = url.pathname.split('/').filter((p) => p !== '');
  if (parts.length !== 1 || !HANDLE.test(parts[0]!)) return refuse;
  return { ok: true, value: `https://www.instagram.com/${parts[0]}` };
}

/** YouTube only: watch?v=, youtu.be/<id>, shorts/<id>. Stored as entered (trimmed). */
export function parseYoutubeInput(input: string): ParseResult {
  const raw = (input ?? '').trim();
  const refuse: ParseResult = {
    ok: false,
    message: 'Use a YouTube link: youtube.com/watch?v=..., youtu.be/... or youtube.com/shorts/...',
  };
  if (raw === '') return refuse;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return refuse;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return refuse;
  const host = url.hostname.toLowerCase().replace(/^www\.|^m\./, '');
  if (host === 'youtu.be') {
    return url.pathname.length > 1 ? { ok: true, value: raw } : refuse;
  }
  if (host === 'youtube.com') {
    if (url.pathname === '/watch' && (url.searchParams.get('v') ?? '') !== '') return { ok: true, value: raw };
    if (/^\/shorts\/[^/]+/.test(url.pathname)) return { ok: true, value: raw };
  }
  return refuse;
}

/** An instagram.com URL or @handle inside the legacy free-text `socials`, or ''. */
export function instagramFromLegacySocials(socials: string | null | undefined): string {
  const text = socials ?? '';
  const url = text.match(/(?:https?:\/\/)?(?:www\.)?instagram\.com\/([A-Za-z0-9._]{1,30})/i);
  if (url) return `https://www.instagram.com/${url[1]}`;
  const at = text.match(/(?:^|[\s,;(])@([A-Za-z0-9._]{1,30})\b/);
  return at ? `https://www.instagram.com/${at[1]}` : '';
}

/**
 * The club's Instagram URL, read-time: the stored `instagramUrl` when it is set, otherwise whatever an
 * old free-text `socials` held. No migration: a doc keeps its `socials` and the next save writes the
 * new field.
 */
export function resolveInstagramUrl(club: { instagramUrl?: string | null; socials?: string | null }): string {
  const own = (club.instagramUrl ?? '').trim();
  return own !== '' ? own : instagramFromLegacySocials(club.socials);
}

/** Comparison key: lower-case, no scheme, no leading www., no trailing slash or punctuation. */
export function urlKey(url: string): string {
  return url
    .trim()
    .replace(/[.,;:!?)\]'"]+$/, '')
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/+$/, '')
    .toLowerCase();
}

const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+/g;
const URL_TOKEN =
  /(?:https?:\/\/|www\.)[^\s<>"']+|\b[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:com|co\.nz|com\.au|net\.au|org\.au|nz|au|me|io|ly|app|net|org)(?:\/[^\s<>"']*)?/gi;

/**
 * Every URL-looking token in a reply, as typed (trailing punctuation trimmed). The club's own email
 * address is not a link and is removed first, otherwise its domain would read as one.
 */
export function urlsIn(text: string, clubEmail = ''): string[] {
  let body = text;
  const email = clubEmail.trim().toLowerCase();
  body = body.replace(EMAIL, (m) => (email !== '' && m.toLowerCase() === email ? ' ' : m));
  const found = body.match(URL_TOKEN) ?? [];
  return found.map((u) => u.replace(/[.,;:!?)\]'"]+$/, '')).filter((u) => u !== '');
}

export interface LinkSources {
  club: { joinUrl?: string; reviewUrl?: string; instagramUrl?: string; socials?: string };
  videos?: ReadonlyArray<{ url: string }>;
}

/** The only URLs a reply may carry, as comparison keys. */
export function allowedUrlKeys(src: LinkSources): Set<string> {
  const keys = new Set<string>();
  const add = (u: string | undefined | null): void => {
    if (u && u.trim() !== '') keys.add(urlKey(u));
  };
  add(src.club.joinUrl);
  add(src.club.reviewUrl);
  add(resolveInstagramUrl(src.club));
  for (const v of src.videos ?? []) add(v.url);
  return keys;
}
